"use client";

import * as React from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  includeTime?: boolean;
}

const DatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  className,
  includeTime = false,
}: DatePickerProps) => {
  // Parse the input value to a Date object if it exists
  const [date, setDate] = React.useState<Date | undefined>(
    value ? parseISO(value) : undefined
  );

  // Update the parent component when the date changes
  const handleSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);

    if (selectedDate) {
      // Format the date as ISO string
      const formattedDate = (
        includeTime ? selectedDate : new Date(selectedDate.setHours(12))
      ).toISOString();
      // Remove the time part if not needed
      const dateString = includeTime
        ? formattedDate
        : formattedDate.split("T")[0];
      onChange(dateString);
    } else {
      onChange("");
    }
  };

  // Display format based on whether we include time or not
  const displayFormat = includeTime ? "PPp" : "PP";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, displayFormat) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="center">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
